import type { ComponentType, ReactNode } from "react"
import { CategoryMaskIcon } from "@/components/ui/category-mask-icon"
import {
  BatteryLowIcon,
  BoneIcon,
  BrainIcon,
  HeartPulseIcon,
  ImpactAreaIcon,
  SaladIcon,
  ScaleIcon,
  StethoscopeIcon,
  VenusAndMarsIcon,
  WindIcon,
} from "@/components/ui/icons"
import {
  BloodCellOrganIcon,
  BonesOrganIcon,
  BrainOrganIcon,
  DigestiveOrganIcon,
  HeartOrganIcon,
  ImmuneOrganIcon,
  KidneysOrganIcon,
  LiverOrganIcon,
  MetabolismOrganIcon,
  ThyroidOrganIcon,
  UterusOrganIcon,
} from "@/components/ui/organ-icons"
import { cmsMediaUrl } from "@/lib/cms"

export type CategoryIconComponent = ComponentType<{
  size?: number
  className?: string
}>

/** Biomarker/disease category id → design-system organ icon. */
export const CATEGORY_ICON_MAP: Record<string, CategoryIconComponent> = {
  // Biomarker taxonomy (current)
  cardiovascular: HeartOrganIcon,
  metabolic: MetabolismOrganIcon,
  liver: LiverOrganIcon,
  pancreas: DigestiveOrganIcon,
  thyroid: ThyroidOrganIcon,
  "kidney-function": KidneysOrganIcon,
  iron: BloodCellOrganIcon,
  "red-blood-cells": BloodCellOrganIcon,
  immune: ImmuneOrganIcon,
  inflammation: ImmuneOrganIcon,
  "bone-muscle": BonesOrganIcon,
  brain: BrainOrganIcon,
  hormones: UterusOrganIcon,
  // Disease taxonomy (current ids after the 15-group restructure)
  kidney: KidneysOrganIcon,
  "blood-disorders": BloodCellOrganIcon,
  endocrine: ThyroidOrganIcon,
  women: UterusOrganIcon,
  // Legacy ids — pre-migration data may still surface these via cached
  // routes; keep entries so the icon resolves rather than falling back to
  // ImpactAreaIcon. Safe to remove after a few weeks post-migration.
  heart: HeartOrganIcon,
  gut: DigestiveOrganIcon,
  "blood-cells": BloodCellOrganIcon,
  metabolism: MetabolismOrganIcon,
}

/**
 * Resolve a category id (== CMS slug) to its organ icon. Falls back to the
 * generic `ImpactAreaIcon` when the category has no dedicated organ icon
 * (e.g. lung, men).
 */
export function getCategoryIcon(id: string): CategoryIconComponent {
  return CATEGORY_ICON_MAP[id] ?? ImpactAreaIcon
}

// ─── Symptom-group icons ─────────────────────────────────────────────────────
//
// The symptoms taxonomy (lib/symptom-groups.ts) uses Lucide stroke icons from
// components/ui/icons.tsx (signature `{ width?, height?, className? }`), keyed
// by the group slug. The CMS may override with an uploaded SVG — resolve that
// via `renderCategoryIcon` (the mask path is icon-shape-agnostic); otherwise
// fall back to this map.

export type SymptomGroupIconComponent = ComponentType<{
  width?: number
  height?: number
  className?: string
}>

/** Symptom-group slug → Lucide icon component (fallback when no CMS icon). */
export const SYMPTOM_GROUP_ICON_MAP: Record<string, SymptomGroupIconComponent> =
  {
    "energy-fatigue": BatteryLowIcon,
    "weight-metabolism": ScaleIcon,
    "mood-sleep-stress": BrainIcon,
    "hormones-sexual-health": VenusAndMarsIcon,
    digestion: SaladIcon,
    "heart-circulation": HeartPulseIcon,
    breathing: WindIcon,
    "pain-bones-muscles": BoneIcon,
  }

/**
 * Resolve a symptom-group slug to its Lucide icon component. Falls back to the
 * generic `StethoscopeIcon` for an unknown slug (both share the
 * `{ width?, height?, className? }` signature).
 */
export function getSymptomGroupIcon(slug: string): SymptomGroupIconComponent {
  return SYMPTOM_GROUP_ICON_MAP[slug] ?? StethoscopeIcon
}

/**
 * The CSS-mask render only works for SVGs — a raster upload (PNG/JPG) would
 * render as a solid-color box. An absent/empty mimeType defaults to render
 * (backward compatible: callers/tests that pass only `{ url }` still mask).
 */
const isMaskableSvg = (mimeType?: string | null) =>
  !mimeType || mimeType.toLowerCase().includes("svg")

/**
 * CMS-aware icon resolver. Prefers an uploaded SVG (rendered as a tinted
 * CSS-mask silhouette); otherwise falls back to the slug→organ-icon map via
 * `getCategoryIcon`. `getCategoryIcon` stays the resolver for code-only callers.
 */
export function renderCategoryIcon(args: {
  categoryId: string
  cmsIcon?: { url: string; mimeType?: string | null } | null
  cmsColor?: string | null
  size?: number
  className?: string
}): ReactNode {
  const { categoryId, cmsIcon, cmsColor, size = 32, className } = args
  if (cmsIcon?.url && isMaskableSvg(cmsIcon.mimeType)) {
    return (
      <CategoryMaskIcon
        url={cmsMediaUrl(cmsIcon.url)}
        color={cmsColor}
        size={size}
        className={className}
      />
    )
  }
  const Icon = getCategoryIcon(categoryId)
  return <Icon size={size} className={className} />
}
